import { forwardRef } from 'react';
import { Send, Square } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ICON_SIZE, Input } from '../../design-system';

/**
 * Shared input row component for AI chat panels
 * Contains textarea and send/stop button
 *
 * @param {Object} props
 * @param {string} props.value - Input value
 * @param {(value: string) => void} props.onChange - Input change handler
 * @param {() => void} props.onSend - Send message handler
 * @param {(e: KeyboardEvent) => void} props.onKeyDown - Keydown handler
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.placeholder - Input placeholder text
 * @param {boolean} props.isStreamingActive - Whether AI is currently streaming
 * @param {() => void} props.onStop - Stop streaming handler
 * @param {React.Ref} ref - Ref forwarded to textarea
 */
export const AIChatInputRow = forwardRef(function AIChatInputRow(
  { value, onChange, onSend, onKeyDown, disabled, placeholder, isStreamingActive, onStop },
  ref
) {
  const { t } = useTranslation('reader');
  return (
    <div className="ai-chat-input-row">
      <Input
        as="textarea"
        variant="quiet"
        aria-label={placeholder}
        ref={ref}
        className="ai-chat-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        rows={1}
      />
      {isStreamingActive ? (
        <button
          className="ai-chat-stop-btn"
          onClick={onStop}
          title={t('aiChat.stopGenerating')}
          aria-label={t('aiChat.stopGenerating')}
        >
          <Square size={ICON_SIZE.xs} />
        </button>
      ) : (
        <button
          className="ai-chat-send-btn"
          aria-label={t('aiPanel.send')}
          onClick={onSend}
          disabled={!value.trim() || disabled}
        >
          <Send size={ICON_SIZE.sm} />
        </button>
      )}
    </div>
  );
});

export default AIChatInputRow;
